import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SaleDiscountPanel from '../components/Sales/SaleDiscountPanel';
import OpenOrderPicker from '../components/Sales/OpenOrderPicker';
import OperationDrawer from './drawer/OperationDrawer';
import useOrderPickerLabel from '../components/Sales/useOrderPickerLabel';
import { resolveContext, offerableOrders } from '../components/Sales/salesDoorContext.mjs';
import { IcoLn } from './ActionIcons';
import './DiscountDoorForm.css';

// W10-7: rebuilt on the shell OperationDrawer anatomy (eyebrow + expand, title, meta, label/value block, hint, Close-left footer, ONE primary lifted from the panel).
// GES-16 'Add Discount' door (sales '+ Data Entry' menu only): ORDER PICKER -> the existing SaleDiscountPanel (thin
// wrapper, same props contract). Context (salesDoorContext.mjs, shared with Add Fees / Make Payment): inside an order
// -> locked, no picker, no 'Change order'; inside a customer -> that customer's open orders + locked customer chip;
// elsewhere -> every customer's open orders. Done orders are never offered (OpenOrderPicker links to the order page).







const FOOT0 = { canSubmit: false, busy: false, invoiced: false, hasValue: false };

// embed = { size, onSize }: the shell's CreateDrawerHost panel carries the frame (draft pill / switch prompt); OperationDrawer renders only the anatomy inside it.
export default function DiscountDoorForm({ size, onSize, onClose, onInteract, params }) {
  const { t } = useTranslation('sales');
  const pickerLabel = useOrderPickerLabel();
  const ctx = resolveContext({ operationId: params?.operationId, partnerId: params?.partner_id, partnerName: params?.partner_name });
  const pinnedOp = ctx.lockedOrder;
  const [opId, setOpId] = useState(pinnedOp);
  const [orders, setOrders] = useState(null);   // null loading · [] empty · rows
  const [loadErr, setLoadErr] = useState(null);
  const [pinnedCash, setPinnedCash] = useState(false); const [pinnedName, setPinnedName] = useState('');
  const [foot, setFoot] = useState(FOOT0); const submitRef = useRef(null);

  const load = useCallback(() => {
    setOrders(null); setLoadErr(null);
    api.getSalesOrders(ctx.listParams)
      .then(d => setOrders(offerableOrders(d?.rows)))
      .catch(e => setLoadErr(e.message || String(e)));
  }, [ctx.lockedPartner]); // eslint-disable-line react-hooks/exhaustive-deps -- listParams derive from lockedPartner only
  useEffect(() => { if (!pinnedOp) load(); }, [pinnedOp, load]);

  // Pinned: the order may be outside the newest 200, so its Cash/Credit nature comes from its own lines.
  useEffect(() => {
    if (!pinnedOp) return;
    api.getSalesOrderLines(pinnedOp).then(d => {
      const l = d?.lines?.[0]; setPinnedCash(l?.payment_method === 'Cash');
      // opened with only an order id (no partner_name param): the customer name comes from the order's partner
      if (!ctx.partnerName && l?.partner_id) api.getPartners().then(ps => { const p = (Array.isArray(ps) ? ps : ps?.rows || []).find(x => x.id === l.partner_id); if (p) setPinnedName(p.name); }).catch(() => {});
    }).catch(() => setPinnedCash(false));
  }, [pinnedOp]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => (orders || []).map(o => ({ value: o.operation_id, label: pickerLabel(o) })), [orders, pickerLabel]);
  const order = (orders || []).find(o => o.operation_id === opId) || null;
  const isCash = pinnedOp ? pinnedCash : order?.payment_method === 'Cash';
  const onChanged = () => {
    window.dispatchEvent(new Event('sales-entry-posted'));   // refreshes the Sales Orders list if it is open
    if (onInteract) onInteract(false);
  };
  // the panel reports its footer state each render; keep the latest submit in a ref and re-render only when a flag changed
  const onFooter = useCallback((f) => { submitRef.current = f.submit; setFoot(p => (p.canSubmit === f.canSubmit && p.busy === f.busy && p.invoiced === f.invoiced && p.hasValue === f.hasValue ? p : { canSubmit: f.canSubmit, busy: f.busy, invoiced: f.invoiced, hasValue: f.hasValue })); }, []);
  const customerName = order?.customer_name || ctx.partnerName || pinnedName || '';
  const why = foot.busy ? t('discount.submitting') : foot.invoiced ? t('discount.invoicedNotice') : !foot.hasValue ? t('discount.needValue') : '';
  const primary = !opId ? null : { label: foot.busy ? t('discount.submitting') : t('discount.submit'), disabled: !foot.canSubmit, why, testId: 'discount-door-submit', onClick: () => submitRef.current && submitRef.current() };
  const changeOrder = opId && !pinnedOp ? <button type="button" className="btn" data-testid="discount-door-change" onClick={() => { setOpId(''); setFoot(FOOT0); }}>{t('discount.door.change')}</button> : null;
  const kv = opId ? [[t('discount.door.order') + (pinnedOp ? ` · ${t('discount.door.locked')}` : ''), opId, 'discount-door-order'], [t('discount.door.customer'), customerName || null]] : [];

  return (
    <OperationDrawer mode="create" embed={{ size, onSize }} onClose={onClose} eyebrow={t('discount.door.eyebrow')} ini={<IcoLn name="discount" />} title={t('discount.door.menu')}
      meta={opId ? `${customerName ? customerName + ' . ' : ''}${opId}` : null} kv={kv} hint={opId ? null : t('discount.door.pickHint')} primary={primary} secondary={changeOrder}>
      {!opId && ctx.lockedPartner && <div className="ddf-8">{ctx.partnerName || ctx.lockedPartner}</div>}
      {opId && <SaleDiscountPanel key={opId} operationId={opId} customerId={ctx.lockedPartner || order?.partner_id || ''} isCash={isCash} onChanged={onChanged} onClose={onClose} onFooter={onFooter} />}
      {!opId && loadErr && (
        <div className="ddf-5">
          <span>{t('discount.door.loadErr', { msg: loadErr })}</span>
          <button type="button" className="btn ddf-6" onClick={load}>{t('discount.retry')}</button>
        </div>
      )}
      {!opId && !loadErr && orders === null && <div className="ddf-7">{t('discount.door.loading')}</div>}
      {!opId && orders && <OpenOrderPicker value="" onChange={v => setOpId(v || '')} placeholder={t('discount.door.searchPh')} options={options} onLeave={onClose} />}
    </OperationDrawer>
  );
}
